import React from 'react';

import { ComboBox, Icon, Item } from '@components';
import { trackClientEvent } from '@utils/posthog';

import { Customer } from '../types';

import Link from './Link';

interface ClientMenuListProps {
  clientList: Customer[];
  canViewDataValidationDashboard: boolean;
  handleChange: (string) => void;
  selectedClientSlug: string;
  currentActiveNav: string;
}

const ClientMenuList = ({
  clientList,
  canViewDataValidationDashboard,
  handleChange,
  selectedClientSlug,
  currentActiveNav,
}: ClientMenuListProps): JSX.Element => {
  const isClientMenuItemShown = selectedClientSlug !== '';

  const clientMenuItems = [
    {
      name: 'Experiment Overview',
      path: `/cl-${selectedClientSlug}/dashboard/`,
      isShown: isClientMenuItemShown,
      isActive: currentActiveNav === 'client-dashboard',
      onClick: () => {
        trackClientEvent(selectedClientSlug, 'Experiment Overview Clicked', {
          tab: 'Summary',
        });
      },
    },
    {
      name: 'Data Validation Dashboard',
      path: `/cl-${selectedClientSlug}/data-validation-dashboard/`,
      isShown: isClientMenuItemShown && canViewDataValidationDashboard,
      isActive: currentActiveNav === 'client-data-validation-dashboard',
      onClick: null,
      icon: <Icon name="SearchPilotMark" size="sm" color={'gray'} />,
      isProtected: true,
    },
    {
      name: 'Archive',
      path: `/cl-${selectedClientSlug}/archive/`,
      isShown: isClientMenuItemShown,
      isActive: currentActiveNav === 'client-archive-dashboard',
      onClick: null,
    },
    {
      name: 'Settings',
      path: `/cl-${selectedClientSlug}/settings/`,
      isShown: isClientMenuItemShown,
      isActive: currentActiveNav === 'client-settings',
      onClick: null,
    },
    {
      name: 'Metrics Explorer',
      path: `/cl-${selectedClientSlug}/metrics-explorer/`,
      isShown: isClientMenuItemShown,
      isDisabled: !selectedClientSlug,
      isActive: currentActiveNav === 'client-metrics-explorer',
    },
  ];

  const clientMenuItemsList = clientMenuItems
    .filter((item) => item.isShown)
    .map((item, index) => {
      return (
        <Link
          key={`${item}-${index}`}
          href={selectedClientSlug !== null ? item.path : undefined}
          disabled={!selectedClientSlug}
          label={item.name}
          isActive={item.isActive}
          onClick={item.onClick ?? undefined}
          rightIcon={item.icon ?? undefined}
        />
      );
    });

  let clientSelector;

  if (clientList !== undefined && clientList.length > 1) {
    clientSelector = (
      <ComboBox
        showBorder={false}
        placeholder="Select a customer"
        defaultItems={clientList}
        onSelectionChange={handleChange}
        selectedKey={selectedClientSlug}
        aria-labelledby={'Customers List Option'}
      >
        {(item) => <Item key={item.slug}>{item.name}</Item>}
      </ComboBox>
    );
  }

  if (clientList.length === 1) {
    clientSelector = clientList.map((client: Customer) => {
      return (
        <h4 key={client.slug} className="px-4 py-2 text-base font-semibold">
          {client.name}
        </h4>
      );
    });
  }

  return (
    <div>
      <div className="flex px-2 py-1">{clientSelector}</div>
      <ul>{clientMenuItemsList}</ul>
    </div>
  );
};

export default ClientMenuList;
